import React, { useMemo, useState } from "react";
import { Mail, Phone, MapPin, Search, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StatusPill, EmptyState } from "@/components/app/primitives";
import { EMPTY_MARK } from "@/lib/format";

const ROLE_LABELS = {
  owner: "Owner", founder: "Founder",
  operating_partner: "Operating partner", advisor: "Advisor",
};

function initials(name = "") {
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "IL";
}

function ContactRow({ icon: Icon, value, onCopy, testId }) {
  if (!value) return null;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onCopy}
      className="group flex w-full items-center justify-between gap-3 rounded-md border border-border bg-background/40 px-3 py-2 text-left hover:bg-white/5 transition-colors duration-150"
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
        <span className="truncate text-sm text-foreground font-mono">{value}</span>
      </span>
      <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground opacity-0 group-hover:opacity-100">Copy</span>
    </button>
  );
}

function ContactDialog({ person, open, onOpenChange }) {
  if (!person) return null;
  const copy = (text, what) => {
    try { navigator.clipboard?.writeText(text); toast.success(`${what} copied`); }
    catch (e) { toast.error("Could not copy"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="org-chart-contact-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{person.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">{person.title || EMPTY_MARK}</span>
            <StatusPill tone="accent" dot={false} label={ROLE_LABELS[person.role] || person.role} />
          </div>
          {person.team_name && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <UsersIcon className="h-4 w-4" strokeWidth={1.75} /> {person.team_name}
            </div>
          )}
          <div className="space-y-2">
            <ContactRow icon={Mail} value={person.email} onCopy={() => copy(person.email, "Email")} testId="org-chart-contact-copy-email" />
            <ContactRow icon={Phone} value={person.phone} onCopy={() => copy(person.phone, "Phone")} testId="org-chart-contact-copy-phone" />
            {person.location && (
              <div className="flex items-center gap-2 rounded-md border border-border bg-background/40 px-3 py-2">
                <MapPin className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <span className="text-sm text-foreground">{person.location}</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Recursive render helper (lowercase, not a component) so React Fast Refresh
// does not instrument a self-referential component.
function renderNode(person, onSelect, highlightId) {
  const highlighted = highlightId && person.id === highlightId;
  return (
    <li key={person.id}>
      <button
        type="button"
        data-testid="org-chart-person-node"
        onClick={() => onSelect(person)}
        className={`inline-flex min-w-[176px] max-w-[220px] flex-col items-start gap-1 rounded-md border bg-card px-3 py-2 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-colors duration-150 hover:border-primary/50 hover:bg-white/5 ${
          highlighted ? "border-[rgba(43,79,216,0.6)] ring-2 ring-[rgba(43,79,216,0.45)]" : "border-border"
        }`}
      >
        <span className="flex items-center gap-2 w-full">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(43,79,216,0.2)] text-[10px] font-semibold text-[#DCE6FF]">
            {initials(person.name)}
          </span>
          <span className="truncate text-sm text-foreground">{person.name}</span>
        </span>
        <span className="truncate text-[11px] text-muted-foreground w-full">{person.title || EMPTY_MARK}</span>
        {person.team_name && (
          <span className="truncate text-[10px] uppercase tracking-[0.1em] text-muted-foreground/80 w-full">{person.team_name}</span>
        )}
      </button>
      {person.children && person.children.length > 0 && (
        <ul>
          {person.children.map((c) => renderNode(c, onSelect, highlightId))}
        </ul>
      )}
    </li>
  );
}

export const OrgChart = ({ roots = [] }) => {
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const highlightId = useMemo(() => {
    if (!q.trim()) return null;
    const needle = q.trim().toLowerCase();
    let found = null;
    const walk = (n) => {
      if (found) return;
      if ((n.name || "").toLowerCase().includes(needle)) { found = n.id; return; }
      (n.children || []).forEach(walk);
    };
    roots.forEach(walk);
    return found;
  }, [q, roots]);

  const onSelect = (p) => { setSelected(p); setOpen(true); };

  if (!roots || roots.length === 0) {
    return <EmptyState message="No people placed on the organisation chart yet." />;
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          <Input
            data-testid="org-chart-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a person"
            className="pl-8 h-9"
          />
        </div>
      </div>
      <ScrollArea className="h-[440px] w-full rounded-md border border-border bg-background/30">
        <div className="org-tree min-w-max p-4">
          <ul>
            {roots.map((r) => renderNode(r, onSelect, highlightId))}
          </ul>
        </div>
      </ScrollArea>
      <ContactDialog person={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
};
